// One reading of backend dates for the V2 pages. The API writes naive timestamps ("2026-04-29T10:00:00") that are UTC,
// and date-only values ("2026-04-29") that are calendar days, so neither may drift by the viewer's time zone.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i

export function isDateOnly(value?: string | null) {
  return !!value && DATE_ONLY.test(value.trim())
}

// The instant a backend value names, or undefined when it names none.
export function parseBackendDate(value?: string | null) {
  const text = value?.trim()
  if (!text) return
  const iso = text.replace(/^(\d{4}-\d{2}-\d{2}) (?=\d)/, "$1T")
  const date = new Date(DATE_ONLY.test(iso) || OFFSET.test(iso) ? iso : `${iso}Z`)
  return Number.isNaN(date.getTime()) ? undefined : date
}

// Milliseconds since the epoch for sorting; 0 for a missing or unreadable value, so those sort last.
export function backendTime(value?: string | null) {
  return parseBackendDate(value)?.getTime() ?? 0
}

// "29 Apr 2026" in the viewer's locale; a date-only value is read in UTC so it stays the day it names; "" when unreadable.
export function formatBackendDate(
  value: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" },
) {
  const date = parseBackendDate(value)
  if (!date) return ""
  return date.toLocaleDateString(locale, isDateOnly(value) ? { ...options, timeZone: "UTC" } : options)
}

// One date format app-wide (lead's decision on P15 / W22): "29 Apr 2026", and "20 Oct 2026, 19:49" with a time
// (24-hour). Day, month, year in that order whatever the viewer's locale; English month names (the app is English).
// A date-only value, and a Date at exactly 00:00 UTC (a data date), are read in UTC so the day never drifts.
const DAY: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }
const toDate = (value: string | Date | null | undefined) => (value instanceof Date ? (Number.isNaN(value.getTime()) ? undefined : value) : parseBackendDate(value))
const utcDay = (value: string | Date | null | undefined, date: Date) =>
  value instanceof Date ? date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0 : isDateOnly(value)
// en-GB writes September as "Sept"; one spelling for every month.
const tidy = (text: string) => text.replace(/\bSept\b/, "Sep")

export function formatDay(value: string | Date | null | undefined) {
  const date = toDate(value)
  if (!date) return ""
  return tidy(date.toLocaleDateString("en-GB", utcDay(value, date) ? { ...DAY, timeZone: "UTC" } : DAY))
}

export function formatDayTime(value: string | Date | null | undefined) {
  const date = toDate(value)
  if (!date) return ""
  if (utcDay(value, date) && !(value instanceof Date)) return formatDay(value)
  return `${tidy(date.toLocaleDateString("en-GB", DAY))}, ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })}`
}

// A row's time in a list whose head says the year (§71): "26 Oct, 10:54" this year, else as formatDayTime.
export function shortDayTime(value: string | Date | null | undefined, now = new Date()) {
  const date = toDate(value)
  const text = formatDayTime(value)
  return date && date.getFullYear() === now.getFullYear() ? text.replace(/ \d{4}(?=,|$)/, "") : text
}

// A period named by its day ("20-10-2026" from the backend, or "2026-10-20") as that day; else undefined, so a real
// period name ("Q1 2026") is shown as it is.
export function periodDay(name: string | null | undefined) {
  const text = name?.trim() ?? ""
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(text)
  const iso = dmy ? `${dmy[3]}-${dmy[2]}-${dmy[1]}` : /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : ""
  return iso ? formatDay(iso) || undefined : undefined
}

// A revision's time in a picker (§44): "Today 14:01" for today (the caller words "Today"), else "20 Oct 2026, 19:49".
export function revisionWhen(value: string | null | undefined, now = new Date()) {
  const date = toDate(value)
  if (!date) return undefined
  const today = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
  return today ? { today: true, text: date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }) } : { today: false, text: formatDayTime(date) }
}
